import { LOCIZE_NS } from '@avanta/shared/constants/common';
import { cn } from '@avanta/shared/utils/cn';
import { memo, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { type CustomTooltipProps } from './types';
import { getTooltipContent } from './utils';

const CustomTooltipComponent = ({
  active,
  payload,
}: CustomTooltipProps): React.ReactElement | null => {
  const { t } = useTranslation(LOCIZE_NS.WELL_BEING);

  const tooltipContent = useMemo(() => {
    if (!active || !payload?.length) return null;
    const data = payload[0].payload;
    return getTooltipContent(data);
  }, [active, payload]);

  const scoreText = useMemo(() => {
    if (!active || !payload?.length) return '';
    const { score } = payload[0].payload;
    return `${t('DailyTracking.End.score.label')}: ${score}`;
  }, [active, payload, t]);

  if (!active || !payload?.length) return null;

  return (
    <div
      className={cn(
        'bg-white border border-gray-200 rounded-lg p-3 shadow-lg',
        'backdrop-blur-sm bg-white/95'
      )}
    >
      <p className={cn('font-medium text-gray-800 mb-1', 'text-sm leading-tight')}>
        {tooltipContent?.title}
      </p>
      <p className={cn('text-teal-600 font-semibold', 'text-base leading-tight')}>{scoreText}</p>
      <p className={cn('text-xs text-gray-500 mt-1', 'leading-relaxed')}>
        {tooltipContent?.subtitle}
      </p>
      {tooltipContent?.showClickHint && (
        <p className={cn('text-xs text-gray-400 mt-1', 'italic')}>
          {t('DailyTracking.Chart.clickToViewDays')}
        </p>
      )}
    </div>
  );
};

export const CustomTooltip = memo(CustomTooltipComponent);
